/**
 * ClinicPerformanceReport — read-only clinic totals for the selected day.
 *
 * Sums every saved performance report (all DDS + HYG) in `clinicData.reports`,
 * the same list FinancialReport edits, so the card updates as each practitioner
 * is saved. PDF Suggested Lab Costs uses the same lab-invoice query (and cache)
 * as FinancialReport, summed across all practitioners. Invisalign Service Charge
 * sums the day's Invisalign invoices with no doctor on them (iTero sleeves,
 * scanner fees) from the same query as the Invisalign Invoices tab.
 */
import { useMemo } from "react";
import { Card } from "@common";
import { useInvisalignInvoices, useLabInvoiceAttachments } from "@api/hooks";
import { formatMoney } from "@utils/format";
import { flattenSuggestedLabInvoiceBreakdowns } from "./FinancialReport";

// [label, total key, hidden-column key, required, computed]
// `computed` totals are not report fields; they are worked out below.
const FIELDS = [
  ["Production", "production", null, true],
  ["Collection", "collection", null, true],
  ["PDF Suggested Lab Costs", "pdf_suggested_lab_costs", "lab_costs", false, true],
  ["Lab Costs", "lab_costs", "lab_costs", false],
  ["Material Costs", "material_costs", "material_costs", false],
  ["Invisalign Costs", "invisalign_costs", "invisalign_costs", false],
  ["Invisalign Service Charge", "invisalign_service_charge", "invisalign_costs", false, true],
  ["Botox", "botox", "botox", false],
  ["Production Adjustment", "production_adjustment", "production_adjustment", false],
  ["Collection Adjustment", "collection_adjustment", "collection_adjustment", false],
  ["Hours Worked", "hours_worked", null, true],
];

const sumCents = (values) =>
  values.reduce((cents, v) => cents + Math.round((Number(v) || 0) * 100), 0) / 100;

export default function ClinicPerformanceReport({
  clinicId,
  date,
  clinicData,
  hiddenColumns = [],
}) {
  const { data: labInvoiceData } = useLabInvoiceAttachments(
    { clinic: clinicId, date },
    { enablePolling: true },
  );
  const { data: invisalignData } = useInvisalignInvoices({ clinic: clinicId, date });

  const reports = useMemo(
    () => (Array.isArray(clinicData?.reports) ? clinicData.reports : []),
    [clinicData?.reports],
  );

  const totals = useMemo(() => {
    const out = {};
    FIELDS.forEach(([, field, , , computed]) => {
      if (!computed) out[field] = sumCents(reports.map((r) => r[field]?.total));
    });
    out.pdf_suggested_lab_costs = sumCents(
      flattenSuggestedLabInvoiceBreakdowns(labInvoiceData, clinicId, date).map(
        (b) => b.total_amount,
      ),
    );
    out.invisalign_service_charge = sumCents(
      (invisalignData?.results || [])
        .filter((invoice) => invoice.is_service_charge)
        .map((invoice) => invoice.total_amount),
    );
    return out;
  }, [reports, labInvoiceData, invisalignData?.results, clinicId, date]);

  const dds = reports.filter((r) => r.practitioner_type?.toLowerCase() === "dds").length;
  const hyg = reports.filter((r) => r.practitioner_type?.toLowerCase() === "hyg").length;

  return (
    <Card className="mt-8">
      <h3 className="text-lg font-semibold mb-1">
        <span className="font-bold">{clinicId}</span>
        <span>{"'s Clinic Performance Report"}</span>
      </h3>
      <p className="mb-4 text-xs text-palette-slate-secondary">
        Total of all DDS / HYG reports for this day ({dds} DDS · {hyg} HYG reported)
      </p>

      <div className="grid gap-4 md:grid-cols-2">
        {FIELDS.filter(([, , hideKey]) => !hideKey || !hiddenColumns.includes(hideKey)).map(
          ([label, field, , required]) => {
            const value = totals[field];
            return (
              <div key={label}>
                <label className="block text-sm font-medium">
                  {label}
                  {required && <span className="text-palette-rose-badge"> *</span>}
                </label>
                <input
                  type="text"
                  value={
                    field === "hours_worked"
                      ? value.toLocaleString("en-US", { maximumFractionDigits: 2 })
                      : formatMoney(value)
                  }
                  disabled
                  readOnly
                  className="mt-2 w-full rounded-xl border border-palette-slate-ring bg-palette-slate-muted px-3 py-2 text-sm text-palette-slate-text"
                />
              </div>
            );
          },
        )}
      </div>
    </Card>
  );
}
